/**
 * @module API
 *
 * Callback-based HTTP client for the Augintel API. Handles token refresh,
 * header construction, response classification, and S3 redirect following.
 *
 * ## Pure functions (fully testable — see src/__tests__/API.test.ts)
 * - {@link buildHeaders} — builds Authorization + X-AuthInfo headers
 * - {@link buildRequest} — builds a fetch RequestInit object
 * - {@link classifyResponse} — interprets status/headers into an action descriptor
 *
 * ## Main entry points
 * - {@link callAPI} — callback-based; used by most components
 * - {@link callAPIAsync} — promise wrapper around callAPI
 * - {@link callInitialApi} — unauthenticated POST (used for SSO token exchange)
 */
import { datadogRum } from '@datadog/browser-rum';
import { refreshToken } from './Cognito';
import { buildLoginRedirectUrl } from './Utils';
import { storage } from '../services/StorageService';
import type {
  LocalStorageUser,
  ImpersonationRecord,
} from '../shared/types/appTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type APISuccessCallback<T = unknown> = (response: T) => void;
export interface APIErrorCallback {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (error: any): void;
}

type ClassifyResponseParams = {
  status: number;
  locationHeader: string | null;
  contentLength: string | null;
  api: string;
};

type ClassifyResponseResult =
  | { action: 'redirect' }
  | { action: 'created' }
  | { action: 's3redirect'; location: string }
  | { action: 'training' }
  | { action: 'text' }
  | { action: 'json' };

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

/**
 * Builds the Authorization and X-AuthInfo headers for a callAPI request.
 * Adds impersonation headers when an impersonated user is present.
 */
export const buildHeaders = (
  refreshedUser: LocalStorageUser,
  impersonatedUser: ImpersonationRecord | null,
): Record<string, string> => {
  const emailHeader = {
    email: refreshedUser.email,
    impersonatedEmail: impersonatedUser?.email,
  };
  const headers: Record<string, string> = {
    Authorization: refreshedUser.idToken.jwtToken,
    'X-AuthInfo': JSON.stringify(emailHeader),
  };
  if (impersonatedUser) {
    headers.impersonatedID = JSON.stringify(impersonatedUser.id);
    headers.impersonatedEmail = impersonatedUser.email;
  }
  return headers;
};

/**
 * Builds the fetch RequestInit object for a callAPI request.
 */
export const buildRequest = (
  headers: Record<string, string>,
  body: unknown,
  method?: string,
): RequestInit & { headers: Record<string, string> } => {
  const request: RequestInit & {
    headers: Record<string, string>;
    body?: string;
    method?: string;
  } = { headers };
  if (body !== null) {
    request.method = method ?? 'POST';
    request.body = JSON.stringify(body);
  } else if (method) {
    request.method = method;
  }
  return request;
};

/**
 * Pure decision tree for interpreting a fetch Response before reading its body.
 */
export const classifyResponse = ({
  status,
  locationHeader,
  contentLength,
  api,
}: ClassifyResponseParams): ClassifyResponseResult => {
  if (status === 401) return { action: 'redirect' };
  if (status === 201) return { action: 'created' };
  if (locationHeader !== null)
    return { action: 's3redirect', location: locationHeader };
  if (status === 202 && contentLength === '0') return { action: 'training' };
  if (api.indexOf('/csv') > 0 && status === 200) return { action: 'text' };
  return { action: 'json' };
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export const getS3Response = <T = unknown>(
  api: string,
  url: string,
  successCallback: APISuccessCallback<T>,
  errorCallback: APIErrorCallback,
): void => {
  try {
    fetch(url)
      .then((res) => res.json())
      .then(
        (resp: { body: string }) => {
          successCallback(
            (api.indexOf('/csv') > 0 ? resp.body : JSON.parse(resp.body)) as T,
          );
        },
        (error: Error) => {
          errorCallback(error);
        },
      );
  } catch (ex) {
    console.error('[API] getS3Response error:', ex);
    if ((ex as Error).message) {
      window.location.href = buildLoginRedirectUrl();
    } else {
      errorCallback(ex);
    }
  }
};

export const pollS3 = <T = unknown>(
  url: string,
  successCallback: APISuccessCallback<T>,
  errorCallback: APIErrorCallback,
): void => {
  try {
    fetch(url)
      .then((res) => {
        if (Number(res.headers.get('content-length')) > 0) {
          return res.json();
        }
        return { empty: true };
      })
      .then(
        (resp: { empty?: boolean; body?: string }) => {
          successCallback(
            (resp.empty ? resp : JSON.parse(resp.body ?? '')) as T,
          );
        },
        (error: Error) => {
          errorCallback(error);
        },
      );
  } catch (ex) {
    if ((ex as Error).message) {
      window.location.href = buildLoginRedirectUrl();
    } else {
      console.error('[API] pollS3 error:', ex);
      errorCallback(ex);
    }
  }
};

export const openWebSocket = (
  openCallback: (ws: WebSocket, event: Event) => void,
  recvCallback: (ws: WebSocket, event: MessageEvent) => void,
  closeCallback: (ws: WebSocket, event: CloseEvent) => void,
  errorCallback: APIErrorCallback,
): void => {
  const user = storage.getUser();
  try {
    if (user) {
      refreshToken(
        user.username,
        user.email,
        user.idToken.jwtToken,
        user.accessToken.jwtToken,
        user.refreshToken.token,
        user.tokenExpiration,
        user.signInExpiration,
        () => {
          const refreshedUser = storage.getUser() ?? ({} as LocalStorageUser);
          const impersonatedUser = storage.getImpersonation();
          const emailHeader = {
            email: refreshedUser.email,
            impersonatedEmail: impersonatedUser?.email,
          };
          const baseUrl = import.meta.env.VITE_WSS_API_URL as string;
          const encodedEmailsStr = encodeURIComponent(
            JSON.stringify(emailHeader),
          );
          const url = `${baseUrl}?token=${refreshedUser.idToken.jwtToken}&xauthinfo=${encodedEmailsStr}`;
          const ws = new WebSocket(url);
          ws.onopen = (event) => {
            openCallback(ws, event);
          };
          ws.onmessage = (event) => {
            recvCallback(ws, event);
          };
          ws.onclose = (event) => {
            closeCallback(ws, event);
          };
        },
      );
    } else {
      window.location.href = buildLoginRedirectUrl();
    }
  } catch (ex) {
    if ((ex as Error).message) {
      window.location.href = buildLoginRedirectUrl();
    } else {
      datadogRum.addError(ex);
      errorCallback(ex);
    }
  }
};

// Caching tracked in Epic 12 (TanStack Query migration).
export const callAPI = async <T = unknown>(
  api: string,
  body: unknown,
  successCallback: APISuccessCallback<T>,
  errorCallback: APIErrorCallback,
  method?: string,
  // eslint-disable-next-line @typescript-eslint/require-await
): Promise<void> => {
  const user = storage.getUser();
  try {
    if (user) {
      refreshToken(
        user.username,
        user.email,
        user.idToken.jwtToken,
        user.accessToken.jwtToken,
        user.refreshToken.token,
        user.tokenExpiration,
        user.signInExpiration,
        () => {
          const refreshedUser = storage.getUser() ?? ({} as LocalStorageUser);
          const impersonatedUser = storage.getImpersonation();
          // URL routing: /admin/* uses VITE_ADMIN_API_URL, all others use VITE_API_URL.
          const url =
            ((api.startsWith('/admin/')
              ? import.meta.env.VITE_ADMIN_API_URL
              : import.meta.env.VITE_API_URL) as string) + api;
          const headers = buildHeaders(refreshedUser, impersonatedUser);
          const request = buildRequest(headers, body, method);
          fetch(url, request)
            .then((res) => {
              const classification = classifyResponse({
                status: res.status,
                locationHeader: res.headers.get('Location'),
                contentLength: res.headers.get('Content-Length'),
                api,
              });
              if (classification.action === 'redirect') {
                window.location.href = buildLoginRedirectUrl();
                return false;
              }
              if (classification.action === 'created') return true;
              if (classification.action === 's3redirect')
                return classification.location;
              if (classification.action === 'training') return res.status;
              // CSV detection is by URL convention (/csv in path).
              return classification.action === 'text' ? res.text() : res.json();
            })
            .then(
              (resp) => {
                if (
                  typeof resp === 'string' &&
                  resp.includes(import.meta.env.VITE_S3_BUCKET as string)
                ) {
                  getS3Response(api, resp, successCallback, errorCallback);
                } else {
                  successCallback(resp as T);
                }
              },
              (error: Error) => {
                console.error('[API] callAPI fetch error:', error.message);
                datadogRum.addError({
                  message: error.message,
                  authInfo: headers['X-AuthInfo'],
                });
                errorCallback(error);
              },
            )
            .catch((reason: Error) => {
              console.error('[API] callAPI fetch catch:', reason.message);
              datadogRum.addError({
                message: reason.message,
                authInfo: headers['X-AuthInfo'],
              });
              errorCallback(reason);
            });
        },
      );
    } else {
      window.location.href = buildLoginRedirectUrl();
    }
  } catch (ex) {
    if ((ex as Error).message) {
      window.location.href = buildLoginRedirectUrl();
    } else {
      datadogRum.addError(ex);
      errorCallback(ex);
    }
  }
};

export const callInitialApi = async <T = unknown>(
  api: string,
  body: unknown,
  successCallback: APISuccessCallback<T>,
  errorCallback: APIErrorCallback,
  // eslint-disable-next-line @typescript-eslint/require-await
): Promise<void> => {
  const url =
    ((api.startsWith('/admin/')
      ? import.meta.env.VITE_ADMIN_API_URL
      : import.meta.env.VITE_API_URL) as string) + api;
  fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  })
    .then((res) => res.json())
    .then((res) => successCallback(res as T))
    .catch((reason: Error) => errorCallback(reason));
};

/**
 * Promise-based wrapper around {@link callAPI}.
 * @template T Shape of the expected response body.
 */
export const callAPIAsync = <T = unknown>(
  api: string,
  body?: unknown,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    void (body !== undefined
      ? callAPI<T>(api, body, resolve, reject)
      : callAPI<T>(api, null, resolve, reject));
  });
